import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider
} from '@mui/material';
import {
  AccountTree,
  TableChart,
  Business,
  Insights,
  Timeline,
  Hub,
  ExpandMore,
  Category
} from '@mui/icons-material';
import { InterviewSummary } from '../types';

interface KnowledgeNode {
  id: string;
  label: string;
  type: 'table' | 'relationship' | 'business_rule' | 'insight';
  category: string;
  connections: string[];
  importance: number;
  confidence: number;
  description?: string;
}

interface KnowledgeVisualizationProps {
  currentKnowledge?: InterviewSummary | null;
  recentQueries: string[];
}

export function KnowledgeVisualization({ 
  currentKnowledge, 
  recentQueries 
}: KnowledgeVisualizationProps) {
  const [viewMode, setViewMode] = useState<'network' | 'hierarchy' | 'timeline'>('network');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedNode, setSelectedNode] = useState<KnowledgeNode | null>(null);

  // Generate knowledge graph from current knowledge
  const knowledgeGraph = useMemo(() => {
    if (!currentKnowledge) return [];

    const nodes: KnowledgeNode[] = [];

    // Add table nodes
    currentKnowledge.tablesDiscovered.forEach((table, index) => {
      nodes.push({
        id: `table_${table}`,
        label: table,
        type: 'table',
        category: 'Database Structure',
        connections: [],
        importance: 0.8,
        confidence: 0.9,
        description: `Database table: ${table}`
      });
    });

    // Add relationship nodes
    currentKnowledge.relationshipsLearned.forEach((relationship, index) => {
      const [source, target] = relationship.split(' → ').map(s => s.trim());
      nodes.push({
        id: `rel_${index}`,
        label: relationship,
        type: 'relationship',
        category: 'Data Relationships',
        connections: [`table_${source}`, `table_${target}`],
        importance: 0.7,
        confidence: 0.85,
        description: `Data relationship: ${relationship}`
      });
    });

    // Add business rule nodes
    currentKnowledge.businessRules.forEach((rule, index) => {
      nodes.push({
        id: `rule_${index}`,
        label: rule,
        type: 'business_rule',
        category: 'Business Logic',
        connections: [],
        importance: 0.6,
        confidence: 0.8,
        description: `Business rule: ${rule}`
      });
    });

    // Add insight nodes
    currentKnowledge.factsLearned.forEach((fact, index) => {
      nodes.push({
        id: `insight_${index}`,
        label: fact,
        type: 'insight',
        category: 'Business Insights',
        connections: [],
        importance: 0.5,
        confidence: 0.75,
        description: `Business insight: ${fact}`
      });
    });

    return nodes;
  }, [currentKnowledge]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = Array.from(new Set(knowledgeGraph.map(node => node.category)));
    return ['all', ...cats];
  }, [knowledgeGraph]);

  // Filter nodes based on selected category
  const filteredNodes = useMemo(() => {
    if (selectedCategory === 'all') return knowledgeGraph;
    return knowledgeGraph.filter(node => node.category === selectedCategory);
  }, [knowledgeGraph, selectedCategory]);

  // Group nodes by type for different views
  const groupedNodes = useMemo(() => {
    return filteredNodes.reduce((acc, node) => {
      if (!acc[node.type]) acc[node.type] = [];
      acc[node.type].push(node);
      return acc;
    }, {} as Record<string, KnowledgeNode[]>);
  }, [filteredNodes]);

  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'table': return <TableChart />;
      case 'relationship': return <AccountTree />;
      case 'business_rule': return <Business />;
      case 'insight': return <Insights />;
      default: return <Hub />;
    }
  };

  const getNodeColor = (type: string) => {
    switch (type) {
      case 'table': return 'primary';
      case 'relationship': return 'success';
      case 'business_rule': return 'warning';
      case 'insight': return 'info';
      default: return 'default';
    }
  };

  const renderNetworkView = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <Hub sx={{ mr: 1 }} />
        Knowledge Network
      </Typography>
      
      {/* Network visualization would go here - for now showing cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
        {filteredNodes.map((node) => (
          <Card 
            key={node.id}
            sx={{ 
              cursor: 'pointer',
              border: selectedNode?.id === node.id ? 2 : 1,
              borderColor: selectedNode?.id === node.id ? 'primary.main' : 'divider'
            }}
            onClick={() => setSelectedNode(node)}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {getNodeIcon(node.type)}
                <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {node.label}
                </Typography>
              </Box>
              <Chip 
                label={node.category} 
                size="small" 
                color={getNodeColor(node.type) as any}
                variant="outlined"
              />
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Confidence: {Math.round(node.confidence * 100)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Importance: {Math.round(node.importance * 100)}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  const renderHierarchyView = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <Category sx={{ mr: 1 }} />
        Knowledge Hierarchy
      </Typography>
      
      {Object.entries(groupedNodes).map(([type, nodes]) => (
        <Accordion key={type} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getNodeIcon(type)}
              <Typography variant="subtitle1" sx={{ ml: 1, textTransform: 'capitalize' }}>
                {type.replace('_', ' ')} ({nodes.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {nodes.map((node) => (
                <ListItem 
                  key={node.id}
                  onClick={() => setSelectedNode(node)}
                  sx={{ 
                    cursor: 'pointer',
                    backgroundColor: selectedNode?.id === node.id ? 'action.selected' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ListItemIcon>
                    {getNodeIcon(node.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={node.label}
                    secondary={`${node.category} • ${Math.round(node.confidence * 100)}% confidence`}
                  />
                  <Chip 
                    label={`${Math.round(node.importance * 100)}%`}
                    size="small"
                    color={getNodeColor(node.type) as any}
                    variant="outlined"
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  const renderTimelineView = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <Timeline sx={{ mr: 1 }} />
        Learning Timeline
      </Typography>
      
      <Box sx={{ position: 'relative', pl: 3 }}>
        {/* Timeline line */}
        <Box sx={{
          position: 'absolute',
          left: 15,
          top: 0,
          bottom: 0,
          width: 2,
          bgcolor: 'primary.main',
          opacity: 0.3
        }} />
        
        {filteredNodes.map((node, index) => (
          <Box key={node.id} sx={{ position: 'relative', mb: 3 }}>
            {/* Timeline dot */}
            <Box sx={{
              position: 'absolute',
              left: -27,
              top: 8,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              border: 2,
              borderColor: 'background.paper'
            }} />
            
            <Paper sx={{ p: 2, ml: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {getNodeIcon(node.type)}
                <Typography variant="body1" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {node.label}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {node.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip 
                  label={node.category} 
                  size="small" 
                  color={getNodeColor(node.type) as any}
                  variant="outlined"
                />
                <Chip 
                  label={`${Math.round(node.confidence * 100)}% confidence`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Paper>
          </Box>
        ))}
      </Box>
    </Box>
  );

  if (!currentKnowledge) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Knowledge Visualization
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete an interview to see your knowledge visualization
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Knowledge Visualization
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredNodes.length} knowledge items
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="network">
                <Hub sx={{ mr: 0.5 }} />
                Network
              </ToggleButton>
              <ToggleButton value="hierarchy">
                <Category sx={{ mr: 0.5 }} />
                Hierarchy
              </ToggleButton>
              <ToggleButton value="timeline">
                <Timeline sx={{ mr: 0.5 }} />
                Timeline
              </ToggleButton>
            </ToggleButtonGroup>
            
            <Divider orientation="vertical" flexItem />
            
            <Typography variant="body2" color="text.secondary">
              Category:
            </Typography>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category === 'all' ? 'All' : category}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                onClick={() => setSelectedCategory(category)}
                size="small"
                clickable
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Main Visualization */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {viewMode === 'network' && renderNetworkView()}
          {viewMode === 'hierarchy' && renderHierarchyView()}
          {viewMode === 'timeline' && renderTimelineView()}
        </CardContent>
      </Card>

      {/* Node Details */}
      {selectedNode && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              {getNodeIcon(selectedNode.type)}
              <Box sx={{ ml: 1 }}>
                Node Details
              </Box>
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              {selectedNode.label}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedNode.description}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Chip 
                label={selectedNode.category} 
                color={getNodeColor(selectedNode.type) as any}
                variant="outlined"
              />
              <Chip 
                label={`${Math.round(selectedNode.confidence * 100)}% confidence`}
                variant="outlined"
              />
              <Chip 
                label={`${Math.round(selectedNode.importance * 100)}% importance`}
                variant="outlined"
              />
            </Box>
            
            {selectedNode.connections.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Connected to:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedNode.connections.map((connectionId) => {
                    const connectedNode = knowledgeGraph.find(n => n.id === connectionId);
                    return connectedNode ? (
                      <Chip
                        key={connectionId}
                        label={connectedNode.label}
                        size="small"
                        variant="outlined"
                        onClick={() => setSelectedNode(connectedNode)}
                        clickable
                      />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
